import { useEffect, useState } from 'react';

interface ServiceWorkerState {
  isRegistered: boolean;
  isSupported: boolean;
  registration: ServiceWorkerRegistration | null;
  error: string | null;
  updateAvailable: boolean;
}

export const useServiceWorker = () => {
  const [state, setState] = useState<ServiceWorkerState>({
    isRegistered: false,
    isSupported: 'serviceWorker' in navigator,
    registration: null,
    error: null,
    updateAvailable: false
  });

  useEffect(() => {
    if (!state.isSupported) {
      console.log('Service Worker não suportado neste browser');
      return;
    }

    registerServiceWorker();
  }, [state.isSupported]);

  const registerServiceWorker = async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });

      console.log('Service Worker registrado:', registration);

      setState(prev => ({
        ...prev,
        isRegistered: true,
        registration,
        error: null
      }));

      // Verificar atualizações
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('Atualização do Service Worker disponível');
              setState(prev => ({
                ...prev,
                updateAvailable: true
              }));
            }
          });
        }
      });

      // Escutar mensagens do SW
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);

    } catch (error) {
      console.error('Erro ao registrar Service Worker:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        isRegistered: false
      }));
    }
  };

  const handleServiceWorkerMessage = (event: MessageEvent) => {
    const { type, payload } = event.data;
    
    switch (type) {
      case 'CACHE_UPDATED':
        console.log('Cache atualizado:', payload);
        break;
      case 'OFFLINE_READY':
        console.log('App pronto para uso offline');
        break;
    }
  };

  // Atualizar Service Worker
  const updateServiceWorker = () => {
    if (state.registration && state.registration.waiting) {
      state.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  };

  // Pré-cache de nome específico
  const precacheName = (name: string) => {
    if (state.registration && state.registration.active) {
      state.registration.active.postMessage({
        type: 'CACHE_NAME_SEARCH',
        payload: { name }
      });
    }
  };

  // Limpar cache específico
  const clearCache = (cacheName?: string) => {
    if (state.registration && state.registration.active) {
      state.registration.active.postMessage({
        type: 'CLEAR_CACHE',
        payload: { cacheName }
      });
    }
  };

  // Verificar status offline
  const isOnline = navigator.onLine;
  
  // Verificar se o app está funcionando offline
  const [isOfflineReady, setIsOfflineReady] = useState(false);
  
  useEffect(() => {
    if (state.isRegistered && !isOnline) {
      setIsOfflineReady(true);
    }
  }, [state.isRegistered, isOnline]);

  return {
    ...state,
    isOnline,
    isOfflineReady,
    updateServiceWorker,
    precacheName,
    clearCache
  };
};